<template>
  <ForwardsPageComponent>
    <div class="d-flex justify-content-between align-items-start gap-3 mb-4">
      <div>
        <h2 class="mb-1">{{ $t("forwards.pap.title") }}</h2>
        <p class="text-body-tertiary mb-0">
          {{ $t("forwards.pap.description") }}
        </p>
      </div>
      <RouterLink
        :to="{ name: 'ForwardsOverview' }"
        class="btn btn-sm btn-outline-secondary"
      >
        {{ $t("forwards.pap.cancel") }}
      </RouterLink>
    </div>

    <LoadingIndicatorComponent
      v-if="loadingMandates"
      :message="$t('forwards.pap.loading')"
    />

    <div v-else-if="loadError" class="error-note" role="alert">
      <FontAwesomeIcon icon="fa-solid fa-exclamation-triangle" />
      <span>{{ loadError }}</span>
      <button
        type="button"
        class="btn btn-sm btn-link ms-auto p-0"
        @click="loadMandates"
      >
        {{ $t("forwards.common.retry") }}
      </button>
    </div>

    <div v-else-if="papMandates.length === 0" class="advisory-note">
      <FontAwesomeIcon icon="fa-solid fa-circle-info" />
      <span>{{ $t("forwards.pap.mandate_empty") }}</span>
    </div>

    <div v-else class="card">
      <WizardSection
        :title="$t('forwards.pap.mandate')"
        :description="$t('forwards.pap.mandate_hint')"
      >
        <div class="row g-3">
          <div class="col-12 col-lg-6">
            <div :id="`${mandateId}-label`" class="form-label">
              {{ $t("forwards.pap.mandate") }}
            </div>
            <div
              class="list-group mandate-options"
              role="radiogroup"
              :aria-labelledby="`${mandateId}-label`"
            >
              <label
                v-for="mandate in papMandates"
                :key="mandate.mandate_id"
                class="list-group-item d-flex align-items-start gap-2"
                :class="mandate.blocked ? 'disabled' : 'list-group-item-action'"
                data-testid="pap-mandate"
              >
                <input
                  v-model="selectedMandateId"
                  class="form-check-input flex-shrink-0 mt-1"
                  type="radio"
                  :name="mandateId"
                  :value="mandate.mandate_id"
                  :disabled="mandate.blocked"
                />
                <span class="d-flex flex-column">
                  <span class="fw-semibold">{{ mandate.name }}</span>
                  <span class="fs-10 text-body-tertiary">
                    {{
                      $t("forwards.pap.mandate_devices", {
                        devices: mandate.device_count,
                        companies: mandate.member_count,
                      })
                    }}
                  </span>
                  <span
                    v-if="mandate.blocked"
                    class="fs-10 text-body-tertiary"
                    data-testid="pap-mandate-blocked-reason"
                  >
                    {{ mandate.blockedReason }}
                  </span>
                </span>
              </label>
            </div>
          </div>
          <div class="col-12 col-lg-6">
            <div v-if="cadenceWarnings.length > 0" class="advisory-note">
              <FontAwesomeIcon icon="fa-solid fa-circle-info" />
              <span data-testid="pap-cadence-note">{{
                cadenceWarnings.join(" ")
              }}</span>
            </div>
          </div>
        </div>
      </WizardSection>

      <WizardSection :title="$t('forwards.pap.name')">
        <div class="row g-3">
          <div class="col-12 col-lg-6">
            <label class="form-label" :for="nameId">
              {{ $t("forwards.pap.name") }}
            </label>
            <input
              :id="nameId"
              v-model="name"
              type="text"
              class="form-control"
              :maxlength="NAME_MAX"
              :placeholder="$t('forwards.pap.name_placeholder')"
            />
          </div>
          <div class="col-12 col-lg-6">
            <label class="form-label" :for="descriptionId">
              {{ $t("forwards.pap.description_label") }}
            </label>
            <input
              :id="descriptionId"
              v-model="description"
              type="text"
              class="form-control"
              :placeholder="$t('forwards.pap.description_placeholder')"
              :aria-describedby="descriptionHelpId"
            />
            <div :id="descriptionHelpId" class="form-text">
              {{ $t("forwards.common.deal_text.description_help") }}
            </div>
          </div>
          <div class="col-12 col-lg-6">
            <label class="form-label" :for="internalNotesId">
              {{ $t("forwards.common.deal_text.internal_notes") }}
            </label>
            <textarea
              :id="internalNotesId"
              v-model="internalNotes"
              class="form-control"
              rows="2"
              :maxlength="INTERNAL_NOTES_MAX"
              :aria-describedby="internalNotesHelpId"
              data-testid="pap-internal-notes"
            ></textarea>
            <div :id="internalNotesHelpId" class="form-text">
              {{ $t("forwards.common.deal_text.internal_notes_help") }}
            </div>
          </div>
          <div v-if="contextState === 'failed'" class="col-12">
            <div
              class="error-note"
              role="alert"
              data-testid="pap-context-failed"
            >
              <FontAwesomeIcon icon="fa-solid fa-exclamation-triangle" />
              <span>{{ contextError }}</span>
              <button
                type="button"
                class="btn btn-sm btn-outline-secondary"
                data-testid="pap-context-retry"
                @click="loadContext"
              >
                {{ $t("forwards.common.retry") }}
              </button>
            </div>
          </div>
        </div>
      </WizardSection>

      <WizardSection
        :title="$t('forwards.pap.window')"
        :description="$t('forwards.pap.unit_price_hint')"
      >
        <div class="row g-3">
          <div class="col-6 col-lg-3">
            <label class="form-label" :for="windowStartId">
              {{ $t("forwards.pap.window_start") }}
            </label>
            <MonthPickerComponent
              v-model="windowStart"
              :inputId="windowStartId"
              data-testid="pap-window-start"
            />
          </div>
          <div class="col-6 col-lg-3">
            <label class="form-label" :for="windowEndId">
              {{ $t("forwards.pap.window_end") }}
            </label>
            <MonthPickerComponent
              v-model="windowEnd"
              :inputId="windowEndId"
              data-testid="pap-window-end"
            />
          </div>
          <div class="col-6 col-lg-3">
            <label class="form-label" :for="priceId">
              {{ $t("forwards.pap.unit_price") }}
            </label>
            <MwhInputComponent
              v-model="unitPrice"
              :id="priceId"
              unit="€/MWh"
              :step="0.001"
            />
          </div>
          <div class="col-6 col-lg-3">
            <label class="form-label" :for="toleranceId">
              {{ $t("forwards.pap.tolerance") }}
            </label>
            <MwhInputComponent
              v-model="tolerancePercent"
              :id="toleranceId"
              unit="%"
              :max="100"
              :step="1"
            />
            <div class="form-text">
              {{ $t("forwards.pap.tolerance_hint") }}
            </div>
          </div>
        </div>

        <p
          v-if="productionRangeNote"
          class="advisory-note mt-3 mb-0"
          data-testid="pap-production-range"
        >
          <FontAwesomeIcon icon="fa-solid fa-circle-info" />
          <span>{{ productionRangeNote }}</span>
        </p>
        <p
          v-if="months.length === 0"
          class="error-note mt-3 mb-0"
          role="alert"
          data-testid="pap-window-invalid"
        >
          {{ windowError }}
        </p>
        <p
          v-else-if="windowOutsideRange"
          class="error-note mt-3 mb-0"
          role="alert"
          data-testid="pap-window-outside-range"
        >
          {{ $t("forwards.pap.window_outside_range") }}
        </p>
      </WizardSection>

      <WizardSection
        v-if="months.length > 0"
        :title="$t('forwards.pap.schedule')"
        :description="$t('forwards.pap.schedule_hint')"
      >
        <div class="row g-2">
          <div
            v-for="month in months"
            :key="month"
            class="col-6 col-md-4 col-lg-3 col-xxl-2"
          >
            <label
              class="form-label fs-10 mb-1"
              :for="`${scheduleId}-${month}`"
            >
              {{ month }}
            </label>
            <MwhInputComponent
              :modelValue="targets[month] ?? null"
              :id="`${scheduleId}-${month}`"
              size="sm"
              data-testid="pap-month"
              @update:modelValue="setTarget(month, $event)"
            />
          </div>
        </div>
        <p class="fw-semibold mt-3 mb-0" data-testid="pap-schedule-total">
          {{
            $t("forwards.pap.schedule_total", {
              value: formatNumberWithSpaces(totalNominated),
              count: months.length,
            })
          }}
        </p>
      </WizardSection>

      <div v-if="selectedMandate?.blocked" class="px-4 pt-3">
        <div
          class="error-note mb-0"
          role="alert"
          data-testid="pap-selected-mandate-blocked"
        >
          <FontAwesomeIcon icon="fa-solid fa-exclamation-triangle" />
          <span>
            {{ selectedMandate.blockedReason }}
            {{ $t("forwards.pap.mandate_blocked_choose_another") }}
          </span>
        </div>
      </div>

      <div v-if="createError" class="px-4 pt-3">
        <div class="error-note mb-0" role="alert">
          <FontAwesomeIcon icon="fa-solid fa-exclamation-triangle" />
          <span>{{ createError }}</span>
        </div>
      </div>

      <WizardFooter>
        <template #start>
          <span v-if="!canCreateDeals" data-testid="pap-create-blocked">
            {{ $t("forwards.common.super_admin_only_create") }}
          </span>
        </template>
        <template #end>
          <RouterLink
            :to="{ name: 'ForwardsOverview' }"
            class="btn btn-link text-secondary"
          >
            {{ $t("forwards.pap.cancel") }}
          </RouterLink>
          <ActionButtonWithSpinner
            :busy="creating"
            :disabled="!canCreate"
            variant="btn-primary"
            size="md"
            data-testid="pap-create"
            @click="createDeal"
          >
            {{
              creating ? $t("forwards.pap.creating") : $t("forwards.pap.create")
            }}
          </ActionButtonWithSpinner>
        </template>
      </WizardFooter>
    </div>
  </ForwardsPageComponent>
</template>

<script setup lang="ts">
import ActionButtonWithSpinner from "@/components/common/ActionButtonWithSpinner.vue";
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import WizardFooter from "@/components/common/WizardFooter.vue";
import WizardSection from "@/components/common/WizardSection.vue";
import { useAccountStore } from "@/composables/account/useAccountStore";
import {
  formatMonthYear,
  formatNumberWithSpaces,
} from "@/composables/formatUtils";
import ForwardsPageComponent from "@/forwards2/components/ForwardsPageComponent.vue";
import MonthPickerComponent from "@/components/common/MonthPickerComponent.vue";
import MwhInputComponent from "@/forwards2/components/MwhInputComponent.vue";
import { dealRoute } from "@/forwards2/deals/dealRoute";
import { forwardsApi, forwardsErrorMessage } from "@/forwards2/forwardsApi";
import { isForwardsStaff } from "@/forwards2/forwardsGuard";
import type {
  PapWizardMandateContext,
  PapWizardMandateOption,
  MandateListRow,
  ProductionRange,
} from "@/forwards2/forwardsTypes";
import {
  currentPeriodLabel,
  nextPeriodLabel,
  periodLastDay,
  periodStartDate,
} from "@/forwards2/periodUtils";
import { toFraction } from "@/forwards2/percentUtils";
import { windowWithinRange } from "@/forwards2/productionRange";
import {
  INTERNAL_NOTES_MAX,
  NAME_MAX,
  shiftPeriodLabel,
} from "@/forwards2/picker/pickerForm";
import { keyBy } from "lodash-es";
import { computed, onMounted, ref, useId, watch } from "vue";
import { RouterLink, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";

/**
 * Creating a pay-as-produced deal: one PAP mandate, a contract window, and a nominated volume per
 * month of that window.
 *
 * A PAP deal has no take table and no criteria — the mandate's devices are its product, exclusively,
 * and the deal buys whatever they produce. What is negotiated instead is the schedule: a target per
 * month, a price that every month is invoiced at, and how far a month may drift before it is
 * flagged. The schedule inputs are laid out from the window, so moving the window rewrites the
 * grid and keeps the targets of the months that survive.
 *
 * A mandate that sells only part of its production (`ProductionRange`) says which months beside the
 * window, and a window outside them holds the create, which the backend would refuse.
 *
 * A sales admin opens it read-only (user decision, 2026-09-28): its reads are `SALES_READ`, the
 * create `SUPER`, so the create is disabled with the reason beside it and never sent.
 */
const { t, locale } = useI18n();
const router = useRouter();
const { globalErrorHandler } = useGlobalHandlers();

/** Beyond this the month grid stops being a form. Mirrors `pay_as_produced_service.MAX_WINDOW_MONTHS`,
 *  which refuses a longer window. */
const MAX_WINDOW_MONTHS = 36;

/** The default window: twelve months starting with the next one. */
const DEFAULT_WINDOW_MONTHS = 12;

const mandateId = useId();
const nameId = useId();
const descriptionId = useId();
const descriptionHelpId = useId();
const internalNotesId = useId();
const internalNotesHelpId = useId();
const windowStartId = useId();
const windowEndId = useId();
const priceId = useId();
const toleranceId = useId();
const scheduleId = useId();

const mandates = ref<MandateListRow[]>([]);
const loadingMandates = ref(false);
const loadError = ref<string | null>(null);

const selectedMandateId = ref<number | null>(null);
const name = ref("");
const description = ref("");
const internalNotes = ref("");
const unitPrice = ref<number | null>(null);
const tolerancePercent = ref<number | null>(10);
const windowStart = ref(defaultWindowStart());
const windowEnd = ref(
  shiftPeriodLabel(defaultWindowStart(), DEFAULT_WINDOW_MONTHS - 1) ??
    defaultWindowStart()
);
const targets = ref<Record<string, number>>({});

/**
 * Whether the chosen mandate's advisories are on screen. The create waits for `"loaded"`: a
 * pay-as-produced deal is created confirmed, so the mandate's verdict and warnings have to have been
 * seen first.
 */
const contextState = ref<"loading" | "loaded" | "failed">("loading");
const contextError = ref<string | null>(null);
/** The chosen mandate's advisories, as the latest context answer gave them; null when there are none to show. */
const mandateContext = ref<PapWizardMandateContext | null>(null);
/** Whether each mandate can take a contract, as the latest context answer judged it. */
const mandateOptions = ref<PapWizardMandateOption[]>([]);

const creating = ref(false);
const createError = ref<string | null>(null);

// The route guard loaded the account before this page opened.
const accountStore = useAccountStore();
const canCreateDeals = computed(() => isForwardsStaff(accountStore.account));

/**
 * The live pay-as-produced mandates the admin route lists, each with the context's verdict on it. A
 * mandate the context has not judged yet — its answer is still loading, or failed — reads as
 * selectable: the create refuses it anyway if it cannot deliver.
 */
const papMandates = computed(() => {
  const verdicts = keyBy(mandateOptions.value, "mandate_id");
  return mandates.value.map((mandate) => ({
    ...mandate,
    blocked: verdicts[mandate.mandate_id]?.can_take_contract === false,
    blockedReason: verdicts[mandate.mandate_id]?.blocked_reason ?? null,
  }));
});

/**
 * A chosen mandate that a later context answer blocks stays chosen: the create is held with the reason
 * beside it, rather than the choice vanishing without a word.
 */
const selectedMandate = computed(
  () =>
    papMandates.value.find(
      (mandate) => mandate.mandate_id === selectedMandateId.value
    ) ?? null
);

/** Every `YYYY-MM` of the window, inclusive; empty when the window is backwards or too long. */
const months = computed(() => {
  if (windowStart.value === "" || windowEnd.value === "") return [];

  const labels: string[] = [];
  let cursor = windowStart.value;
  for (let step = 0; step < MAX_WINDOW_MONTHS; step += 1) {
    labels.push(cursor);
    if (cursor === windowEnd.value) return labels;
    const next = nextPeriodLabel(cursor);
    if (next === null) return [];
    cursor = next;
  }
  return [];
});

const windowError = computed(() => {
  if (windowStart.value === "" || windowEnd.value === "") {
    return t("forwards.pap.window_missing");
  }
  if (windowEnd.value < windowStart.value) {
    return t("forwards.pap.window_invalid");
  }
  return t("forwards.pap.window_too_long", { count: MAX_WINDOW_MONTHS });
});

/** Sentences from the server about the chosen mandate's registries; shown as they are. */
const cadenceWarnings = computed(
  () => mandateContext.value?.cadence_warnings ?? []
);

/** Open on both ends when there is no mandate context. */
const productionRange = computed<ProductionRange>(
  () =>
    mandateContext.value ?? { production_from: null, production_until: null }
);

const productionRangeNote = computed(() => {
  const { production_from: from, production_until: until } =
    productionRange.value;
  const month = (date: string) => formatMonthYear(date, locale.value);
  if (from !== null && until !== null)
    return t("forwards.pap.production_range_from_until", {
      from: month(from),
      until: month(until),
    });
  if (from !== null)
    return t("forwards.pap.production_range_from", { from: month(from) });
  if (until !== null)
    return t("forwards.pap.production_range_until", { until: month(until) });
  return null;
});

const windowOutsideRange = computed(
  () =>
    months.value.length > 0 &&
    !windowWithinRange(
      months.value[0],
      months.value[months.value.length - 1],
      productionRange.value
    )
);

const totalNominated = computed(() =>
  months.value.reduce((sum, month) => sum + (targets.value[month] ?? 0), 0)
);

const canCreate = computed(
  () =>
    canCreateDeals.value &&
    contextState.value === "loaded" &&
    selectedMandate.value !== null &&
    !selectedMandate.value.blocked &&
    name.value.trim().length > 0 &&
    unitPrice.value !== null &&
    unitPrice.value > 0 &&
    months.value.length > 0 &&
    !windowOutsideRange.value
);

const loadMandates = async () => {
  loadingMandates.value = true;
  try {
    // The admin route, not the account-scoped one: back office cuts contracts for producers the
    // acting admin is not a member of. It filters to the live pay-as-produced mandates itself.
    const response = await forwardsApi.listAdminMandates({ isPap: true });
    mandates.value = response.data.rows;
    loadError.value = null;
  } catch (error) {
    loadError.value =
      forwardsErrorMessage(error) ?? t("forwards.pap.load_failed");
  } finally {
    loadingMandates.value = false;
  }
};

// Only the newest request may write: a slow answer for the previous mandate must not overwrite the
// current one's advisories, nor a stale failure blank them.
let contextGeneration = 0;

/**
 * The chosen mandate's advisories and every mandate's verdict, in one answer from the admin route, judged
 * on the acting admin platform-wide like the mandate list, so it is there while managing a client
 * account too.
 */
const loadContext = async () => {
  const mandateId = selectedMandateId.value;
  const generation = ++contextGeneration;
  contextState.value = "loading";
  try {
    const response = await forwardsApi.getPapWizardContext(mandateId);
    if (
      generation !== contextGeneration ||
      mandateId !== selectedMandateId.value
    )
      return;
    mandateOptions.value = response.data.mandate_options;
    mandateContext.value = response.data.mandate;
    contextError.value = null;
    contextState.value = "loaded";
  } catch (error) {
    if (
      generation !== contextGeneration ||
      mandateId !== selectedMandateId.value
    )
      return;
    mandateContext.value = null;
    contextError.value =
      forwardsErrorMessage(error) ?? t("forwards.pap.context_failed");
    contextState.value = "failed";
    globalErrorHandler(error);
  }
};

const setTarget = (month: string, value: number | null) => {
  const next = { ...targets.value };
  if (value === null) {
    delete next[month];
  } else {
    next[month] = value;
  }
  targets.value = next;
};

const createDeal = async () => {
  if (!canCreate.value || selectedMandateId.value === null) return;

  creating.value = true;
  createError.value = null;
  try {
    const response = await forwardsApi.createPapDeal({
      mandate_id: selectedMandateId.value,
      name: name.value.trim(),
      description: description.value.trim() || null,
      internal_notes: internalNotes.value.trim() || null,
      unit_price: unitPrice.value!.toFixed(3),
      window_start: periodStartDate(months.value[0])!,
      window_end: periodLastDay(months.value[months.value.length - 1])!,
      // Every month of the window must be present; an untouched input nominates nothing.
      monthly_targets: Object.fromEntries(
        months.value.map((month) => [month, targets.value[month] ?? 0])
      ),
      volume_tolerance: toFraction(tolerancePercent.value ?? 0),
    });
    await router.push(dealRoute(response.data.deal.id));
  } catch (error) {
    createError.value =
      forwardsErrorMessage(error) ?? t("forwards.pap.create_failed");
  } finally {
    creating.value = false;
  }
};

watch(selectedMandateId, () => {
  void loadContext();
});

onMounted(() => {
  void loadMandates();
  void loadContext();
});

function defaultWindowStart(): string {
  return (
    nextPeriodLabel(currentPeriodLabel("month")) ?? currentPeriodLabel("month")
  );
}
</script>

<style scoped lang="scss">
.mandate-options {
  max-height: 16rem;
  overflow-y: auto;
}
</style>
